import { Grid, Typography } from '@mui/material';
import React from 'react';
import { useFormContext, UseFormSetValue, useWatch } from 'react-hook-form';
import AddressAutocomplete from '../../../../shared/AddressAutocomplete';
import FormTextField from '../../../../shared/FormTextField';
import { Address } from '../../../../types/generic';

export const getAirportDisplayLabel = (airport: {
    name: string;
    code?: string;
}): string => {
    return airport?.code ? `${airport?.name} (${airport.code})` : airport?.name;
};

export const handleAirport = (
    address: Address,
    fieldPath: string,
    setValue: UseFormSetValue<any>,
) => {
    setValue(`${fieldPath}.name`, address.name, {
        shouldDirty: true,
        shouldValidate: true,
    });
    let code = undefined;
    if (address.displayLabel) {
        code = extractIataCode(address.displayLabel);
    }
    setValue(`${fieldPath}.code`, code);
};

function extractIataCode(text: string): string {
    const match = text.match(/\(([A-Z]{3})\)/);
    return match?.[1] || '';
}

const FlightForm = () => {
    const {
        setValue,
        control,
        formState: { errors },
    } = useFormContext();
    const departureAirport = useWatch({
        control,
        name: 'itineraryItemInfo.departureAirport',
    });
    const arrivalAirport = useWatch({
        control,
        name: 'itineraryItemInfo.arrivalAirport',
    });

    const departureAirportError = (errors as any).itineraryItemInfo
        ?.departureAirport?.name?.message;
    const arrivalAirportError = (errors as any).itineraryItemInfo
        ?.arrivalAirport?.name?.message;

    return (
        <Grid item container spacing={2}>
            <Grid item container spacing={2}>
                <Grid item xs={12} sm={6}>
                    <FormTextField
                        name='itineraryItemInfo.airline'
                        control={control}
                        label='Airline'
                        placeholder='e.g. Qantas'
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <FormTextField
                        name='itineraryItemInfo.flightNumber'
                        control={control}
                        label='Flight Number'
                        placeholder='e.g. QF1'
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <FormTextField
                        name='itineraryItemInfo.gate'
                        control={control}
                        label='Gate'
                        placeholder='e.g. 12'
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <FormTextField
                        name='itineraryItemInfo.seatNumber'
                        control={control}
                        label='Seat Number'
                        placeholder='e.g. 14A'
                    />
                </Grid>
            </Grid>
            <Grid item xs={12}>
                <Typography variant='body3' sx={{ fontWeight: 600 }}>
                    Departure Airport *
                </Typography>
            </Grid>
            <Grid container item spacing={2}>
                <Grid item xs={12} sm={6}>
                    <AddressAutocomplete
                        height='4px'
                        key='departure-airport-autocomplete'
                        error={Boolean(departureAirportError)}
                        helperText={departureAirportError as string}
                        onChange={(address) =>
                            handleAirport(
                                address,
                                'itineraryItemInfo.departureAirport',
                                setValue,
                            )
                        }
                        initialInputValue={getAirportDisplayLabel(
                            departureAirport,
                        )}
                        options={{
                            fields: [
                                'address_components',
                                'geometry',
                                'name',
                                'formatted_address',
                                'place_id',
                            ],
                            types: ['airport'],
                            componentRestrictions: { country: 'au' },
                        }}
                        placeholder='Search airport...'
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <FormTextField
                        name='itineraryItemInfo.departureAirport.terminal'
                        control={control}
                        label='Terminal'
                        placeholder='T1'
                    />
                </Grid>
            </Grid>
            <Grid item xs={12}>
                <Typography variant='body3' sx={{ fontWeight: 600 }}>
                    Arrival Airport *
                </Typography>
            </Grid>
            <Grid item container spacing={2}>
                <Grid item xs={12} sm={6}>
                    <AddressAutocomplete
                        height='4px'
                        key='arrival-airport-autocomplete'
                        error={Boolean(arrivalAirportError)}
                        helperText={arrivalAirportError as string}
                        onChange={(address) =>
                            handleAirport(
                                address,
                                'itineraryItemInfo.arrivalAirport',
                                setValue,
                            )
                        }
                        initialInputValue={getAirportDisplayLabel(
                            arrivalAirport,
                        )}
                        options={{
                            fields: [
                                'address_components',
                                'geometry',
                                'name',
                                'formatted_address',
                                'place_id',
                            ],
                            types: ['airport'],
                            componentRestrictions: { country: 'au' },
                        }}
                        placeholder='Search airport...'
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <FormTextField
                        name='itineraryItemInfo.arrivalAirport.terminal'
                        control={control}
                        label='Terminal'
                        placeholder='T2'
                    />
                </Grid>
            </Grid>
        </Grid>
    );
};

export default FlightForm;
